import {
  Eye,
  HandCoins,
  Info,
  LoaderCircle,
  RefreshCw,
  Wallet,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

import EstadoFinancieroBadge from '../../components/cuentasPorPagar/EstadoFinancieroBadge'
import RegistrarPagoModal from '../../components/cuentasPorPagar/RegistrarPagoModal'
import {
  CampoFiltro,
  estiloInputFiltro,
} from '../../components/pos/FiltrosPosComunes'
import Paginacion from '../../components/ui/Paginacion'
import { useEmpresa } from '../../context/EmpresaContext'
import { usePaginacion } from '../../hooks/usePaginacion'
import { cargarCuentasPorPagar } from '../../services/cuentasPorPagar'
import { obtenerModalidad } from '../../utils/abastecimiento'
import { nombreProveedorCompra } from '../../utils/compras'
import {
  ESTADOS_FINANCIEROS,
  filaAdmitePago,
  filtrarCuentas,
  puedeRegistrarPagoCompra,
  resumirCuentas,
} from '../../utils/cuentasPorPagar'
import {
  formatearFechaPos,
  formatearMontoPos,
} from '../../utils/pos'

const NO_DISPONIBLE = '—'

function montoOGuion(centavos) {
  return centavos === null || centavos === undefined
    ? NO_DISPONIBLE
    : formatearMontoPos(centavos / 100)
}

function Indicador({
  titulo,
  valor,
  detalle,
  clase = 'border-slate-200 bg-white',
}) {
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${clase}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {titulo}
      </p>

      <p className="mt-2 truncate text-2xl font-bold tabular-nums text-slate-900">
        {valor}
      </p>

      {detalle && (
        <p className="mt-1 text-xs text-slate-500">{detalle}</p>
      )}
    </div>
  )
}

function CuentasPorPagar() {
  const navigate = useNavigate()
  const { empresaActual } = useEmpresa()

  const empresaId = empresaActual?.id
  const puedePagar = puedeRegistrarPagoCompra(empresaActual?.rol)

  const [filas, setFilas] = useState([])
  const [truncado, setTruncado] = useState(false)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [filaPago, setFilaPago] = useState(null)

  const [filtroProveedor, setFiltroProveedor] = useState('')
  const [filtroDesde, setFiltroDesde] = useState('')
  const [filtroHasta, setFiltroHasta] = useState('')
  const [filtroModalidad, setFiltroModalidad] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('')
  const [soloVencidas, setSoloVencidas] = useState(false)

  const cargar = useCallback(async () => {
    if (!empresaId) return

    setCargando(true)
    setError('')

    try {
      const resultado = await cargarCuentasPorPagar(empresaId)

      setFilas(resultado.filas)
      setTruncado(resultado.truncado)
    } catch (errorCarga) {
      console.error('Error al cargar cuentas por pagar:', errorCarga)
      setError(
        errorCarga.message || 'No fue posible cargar las cuentas por pagar.'
      )
    } finally {
      setCargando(false)
    }
  }, [empresaId])

  useEffect(() => {
    setFiltroProveedor('')
    cargar()
  }, [cargar])

  const proveedores = useMemo(() => {
    const mapa = new Map()

    filas.forEach(({ compra }) => {
      if (compra.proveedor_id && !mapa.has(compra.proveedor_id)) {
        mapa.set(compra.proveedor_id, nombreProveedorCompra(compra))
      }
    })

    return [...mapa.entries()].sort((a, b) =>
      a[1].localeCompare(b[1], 'es')
    )
  }, [filas])

  const filasFiltradas = useMemo(
    () =>
      filtrarCuentas(filas, {
        proveedorId: filtroProveedor,
        desde: filtroDesde,
        hasta: filtroHasta,
        modalidad: filtroModalidad,
        estado: filtroEstado,
        soloVencidas,
      }),
    [
      filas,
      filtroProveedor,
      filtroDesde,
      filtroHasta,
      filtroModalidad,
      filtroEstado,
      soloVencidas,
    ]
  )

  const resumen = useMemo(
    () => resumirCuentas(filasFiltradas),
    [filasFiltradas]
  )

  const filtrosKey = [
    empresaId,
    filtroProveedor,
    filtroDesde,
    filtroHasta,
    filtroModalidad,
    filtroEstado,
    soloVencidas,
  ].join('|')

  const {
    pagina,
    setPagina,
    tamanioPagina,
    setTamanioPagina,
    totalPaginas,
    indiceInicio,
    indiceFin,
  } = usePaginacion({
    totalRegistros: filasFiltradas.length,
    filtrosKey,
  })

  const filasPaginadas = filasFiltradas.slice(
    (pagina - 1) * tamanioPagina,
    pagina * tamanioPagina
  )

  const hayFiltros = Boolean(
    filtroProveedor ||
      filtroDesde ||
      filtroHasta ||
      filtroModalidad ||
      filtroEstado ||
      soloVencidas
  )

  const limpiarFiltros = () => {
    setFiltroProveedor('')
    setFiltroDesde('')
    setFiltroHasta('')
    setFiltroModalidad('')
    setFiltroEstado('')
    setSoloVencidas(false)
  }

  const verDetalle = (fila) => {
    navigate(`/contabilidad/cuentas-por-pagar/${fila.compra.id}`)
  }

  const puedeRegistrarEn = (fila) => puedePagar && filaAdmitePago(fila)

  const renderAcciones = (fila, movil) => {
    const clase = movil
      ? 'flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition'
      : 'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition'

    return (
      <>
        {puedeRegistrarEn(fila) && (
          <button
            type="button"
            onClick={() => setFilaPago(fila)}
            className={`${clase} border-emerald-600 text-emerald-700 hover:bg-emerald-50`}
          >
            <HandCoins size={16} />
            Registrar pago
          </button>
        )}

        <button
          type="button"
          onClick={() => verDetalle(fila)}
          className={`${clase} border-slate-300 text-slate-600 hover:bg-slate-100`}
        >
          <Eye size={16} />
          Ver detalle
        </button>
      </>
    )
  }

  const renderContenido = () => {
    if (cargando && filas.length === 0) {
      return (
        <div className="flex min-h-64 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
          <LoaderCircle
            size={32}
            className="animate-spin text-emerald-600"
          />
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <h3 className="text-lg font-bold text-red-600">
            No fue posible cargar las cuentas por pagar
          </h3>

          <p className="mt-2 text-slate-600">{error}</p>
        </div>
      )
    }

    if (filasFiltradas.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <Wallet
            size={36}
            className="mx-auto text-slate-300"
          />

          <h3 className="mt-3 text-lg font-bold text-slate-900">
            {hayFiltros
              ? 'No hay cuentas con estos filtros'
              : 'No hay compras con obligación de pago'}
          </h3>

          {hayFiltros && (
            <button
              type="button"
              onClick={limpiarFiltros}
              className="mt-4 min-h-11 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      )
    }

    return (
      <>
        {truncado && (
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Hay más compras de las que se pueden cargar a la vez; los
            indicadores podrían estar incompletos. Contacta a soporte.
          </p>
        )}

        {/* Mobile: tarjetas */}
        <div className="space-y-3 md:hidden">
          {filasPaginadas.map((fila) => {
            const { compra } = fila
            const modalidad = obtenerModalidad(compra.modalidad)

            return (
              <div
                key={compra.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900">
                      {compra.codigo_compra}
                    </p>

                    <p className="text-xs text-slate-500">
                      {formatearFechaPos(compra.fecha_compra)}
                    </p>
                  </div>

                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${modalidad.clase}`}
                  >
                    {modalidad.etiqueta}
                  </span>
                </div>

                <p className="mt-2 truncate font-semibold text-slate-900">
                  {nombreProveedorCompra(compra)}
                </p>

                <div className="mt-2">
                  <EstadoFinancieroBadge fila={fila} />
                </div>

                <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <div className="min-w-0">
                    <dt className="text-xs text-slate-500">
                      {fila.clasificacion.tipo === 'COMPROMISO' ||
                      !fila.clasificacion.definitivo
                        ? 'Total est.'
                        : 'Total'}
                    </dt>
                    <dd className="font-semibold tabular-nums">
                      {formatearMontoPos(fila.totalCentavos / 100)}
                    </dd>
                  </div>

                  <div className="min-w-0">
                    <dt className="text-xs text-slate-500">Abonado</dt>
                    <dd className="font-semibold tabular-nums">
                      {montoOGuion(fila.abonadoCentavos)}
                    </dd>
                  </div>

                  <div className="min-w-0">
                    <dt className="text-xs text-slate-500">Saldo</dt>
                    <dd className="font-bold tabular-nums text-emerald-700">
                      {montoOGuion(fila.saldoCentavos)}
                    </dd>
                  </div>
                </dl>

                <p className="mt-2 text-xs text-slate-500">
                  Vencimiento: {formatearFechaPos(fila.vencimiento)}
                </p>

                <div className="mt-3 flex gap-2">
                  {renderAcciones(fila, true)}
                </div>
              </div>
            )
          })}
        </div>

        {/* Tablet / desktop: tabla */}
        <div className="hidden w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:block">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-3">Compra</th>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3">Proveedor</th>
                <th className="py-2 pr-3">Modalidad</th>
                <th className="py-2 pr-3 text-right">Total</th>
                <th className="py-2 pr-3 text-right">Abonado</th>
                <th className="py-2 pr-3 text-right">Saldo</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3">Vencimiento</th>
                <th className="py-2 text-right">Acciones</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {filasPaginadas.map((fila) => {
                const { compra } = fila
                const modalidad = obtenerModalidad(compra.modalidad)
                const estimado =
                  fila.clasificacion.tipo === 'COMPROMISO' ||
                  !fila.clasificacion.definitivo

                return (
                  <tr key={compra.id}>
                    <td className="whitespace-nowrap py-2 pr-3 font-semibold text-slate-900">
                      {compra.codigo_compra}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3">
                      {formatearFechaPos(compra.fecha_compra)}
                    </td>
                    <td className="max-w-56 truncate py-2 pr-3">
                      {nombreProveedorCompra(compra)}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${modalidad.clase}`}
                      >
                        {modalidad.etiqueta}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                      {formatearMontoPos(fila.totalCentavos / 100)}
                      {estimado && (
                        <span className="block text-xs text-slate-400">
                          {fila.clasificacion.tipo === 'COMPROMISO'
                            ? 'estimado'
                            : 'provisional'}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                      {montoOGuion(fila.abonadoCentavos)}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 text-right font-semibold tabular-nums text-slate-900">
                      {montoOGuion(fila.saldoCentavos)}
                    </td>
                    <td className="py-2 pr-3">
                      <EstadoFinancieroBadge fila={fila} />
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3">
                      {formatearFechaPos(fila.vencimiento)}
                    </td>
                    <td className="py-2">
                      <div className="flex justify-end gap-2">
                        {renderAcciones(fila, false)}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <Paginacion
          pagina={pagina}
          totalPaginas={totalPaginas}
          tamanioPagina={tamanioPagina}
          totalRegistros={filasFiltradas.length}
          indiceInicio={indiceInicio}
          indiceFin={indiceFin}
          etiquetaRegistros="cuentas"
          onCambiarPagina={setPagina}
          onCambiarTamanio={setTamanioPagina}
        />
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:mb-8 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Contabilidad
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Cuentas por pagar
          </h2>

          <p className="mt-2 text-slate-500">
            Obligaciones con proveedores de{' '}
            {empresaActual?.nombre_fantasia}, según las compras.
          </p>
        </div>

        <button
          type="button"
          onClick={cargar}
          disabled={cargando}
          className="flex min-h-12 items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw
            size={18}
            className={cargando ? 'animate-spin' : ''}
          />
          Actualizar
        </button>
      </div>

      <div className="mb-6 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <Info
          size={20}
          className="mt-0.5 shrink-0"
        />

        <p>
          Los abonos y saldos corresponden solo a pagos registrados en
          Franco Frutt. Las compras históricas pueden haberse pagado fuera
          del sistema: requieren una conciliación inicial antes de
          considerar el saldo como deuda real. Solo se pueden pagar
          compras con recepción validada.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Indicador
          titulo="Total cuentas por pagar"
          valor={formatearMontoPos(resumen.totalCentavos / 100)}
          detalle={`${resumen.cantidadObligaciones} compras con deuda confirmada · ${resumen.cantidadProvisionales} provisionales aparte`}
        />

        <Indicador
          titulo="Pendiente confirmado"
          valor={montoOGuion(resumen.pendienteCentavos)}
        />

        <Indicador
          titulo="Total abonado"
          valor={montoOGuion(resumen.abonadoCentavos)}
        />

        <Indicador
          titulo="Total vencido"
          valor={montoOGuion(resumen.vencidoCentavos)}
          detalle={`${resumen.cantidadVencidas} compras vencidas`}
        />

        <Indicador
          titulo="Compromisos estimados"
          valor={formatearMontoPos(resumen.compromisosCentavos / 100)}
          detalle={`${resumen.cantidadCompromisos} compras previas sin recepción · no es deuda`}
          clase="border-violet-200 bg-violet-50"
        />
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <CampoFiltro label="Proveedor">
            <select
              value={filtroProveedor}
              onChange={(event) => setFiltroProveedor(event.target.value)}
              className={estiloInputFiltro}
            >
              <option value="">Todos</option>
              {proveedores.map(([id, nombre]) => (
                <option
                  key={id}
                  value={id}
                >
                  {nombre}
                </option>
              ))}
            </select>
          </CampoFiltro>

          <CampoFiltro label="Fecha desde">
            <input
              type="date"
              value={filtroDesde}
              onChange={(event) => setFiltroDesde(event.target.value)}
              className={estiloInputFiltro}
            />
          </CampoFiltro>

          <CampoFiltro label="Fecha hasta">
            <input
              type="date"
              value={filtroHasta}
              onChange={(event) => setFiltroHasta(event.target.value)}
              className={estiloInputFiltro}
            />
          </CampoFiltro>

          <CampoFiltro label="Modalidad">
            <select
              value={filtroModalidad}
              onChange={(event) => setFiltroModalidad(event.target.value)}
              className={estiloInputFiltro}
            >
              <option value="">Todas</option>
              <option value="DIRECTA">Directa</option>
              <option value="PREVIA">Previa</option>
            </select>
          </CampoFiltro>

          <CampoFiltro label="Estado de pago">
            <select
              value={filtroEstado}
              onChange={(event) => setFiltroEstado(event.target.value)}
              className={estiloInputFiltro}
            >
              <option value="">Todos</option>
              {Object.entries(ESTADOS_FINANCIEROS).map(([valor, estado]) => (
                <option
                  key={valor}
                  value={valor}
                >
                  {estado.etiqueta}
                </option>
              ))}
            </select>
          </CampoFiltro>

          <label className="flex min-h-11 items-center gap-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={soloVencidas}
              onChange={(event) => setSoloVencidas(event.target.checked)}
              className="h-5 w-5 accent-emerald-600"
            />
            Solo vencidas
          </label>
        </div>

        {hayFiltros && (
          <div className="mt-4 flex justify-end border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={limpiarFiltros}
              className="min-h-11 text-sm font-semibold text-slate-500 hover:text-red-600"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {renderContenido()}

      <RegistrarPagoModal
        abierto={Boolean(filaPago)}
        fila={filaPago}
        empresaId={empresaId}
        onCerrar={() => setFilaPago(null)}
        onRegistrado={() => {
          setFilaPago(null)
          cargar()
        }}
      />
    </div>
  )
}

export default CuentasPorPagar
